// ARKit-52 → streaming-server control-rig (live lip-sync for the in-world
// OGS-streaming avatar).
//
// The streaming server consumes a MetaHuman control-rig vector (Myra head = 168,
// Isaac = 60) — the SAME space as the on-device rig2mesh input. The conversation
// streams 52-element ARKit frames (AudioAnimationBuffer.getCurrentArkit). To drive
// the streamed avatar we reuse the exact ARKit→188 MetaHuman mapping the rig2mesh
// was trained with (arkitToMh) and gather the N controls the character needs.
//
// The 188→N gather is a per-character build-time artifact (in the character's
// /ogs bundle) whose ORDER matches the server's rig_names.json
// (verified: the MetaHuman index_168_to_188 map → 168 names == the bundle's
// rig_names.json). This module is pure + dependency-light so the caller
// (StreamAvatarModel) loads the gather JSON and passes it in — trivially testable.

import { arkitToMh, MH_LEN } from './arkitToMh.js'

/**
 * Map a live 52-element ARKit frame to a character's N-control streaming rig.
 *
 * @param {ArrayLike<number>} arkit52 - live ARKit weights (length >= 52)
 * @param {number[]} gather - N indices into the 188 MH controls (per-character)
 * @param {Float32Array} outN - destination, length N === gather.length (overwritten)
 * @param {Float32Array} [scratch188] - reusable 188 buffer to avoid a hot-path alloc
 * @returns {Float32Array} outN
 */
export function arkitToStreamRig(arkit52, gather, outN, scratch188) {
  const buf = scratch188 || new Float32Array(MH_LEN)
  arkitToMh(arkit52, buf)
  for (let i = 0; i < gather.length; i++) outN[i] = buf[gather[i]]
  return outN
}

// The per-character 188→N gather (order matches the server's rig_names.json) is no
// longer hardcoded here — it ships as the character's `rig_gather` row in
// the character's /ogs bundle and is resolved via buildOGSSceneResolver().gatherUrl
// (see StreamAvatarModel). This module is just the pure ARKit→gathered-rig math.
